import { useAuthState } from '@auth';
import { CommunityAuthRequired, CommunityErrorState, LoadingMessage as CommunityLoadingState } from '@communities/community/components';
import { CommunityDialog } from '@communities/community/components/CommunityDialog';
import { useCommunityDetail, useDeleteCommunity } from '@communities/community/hooks';
import { EntityType } from '@communities/content-management/domain';
import { TabNavigation } from '@communities/content-management/pages';
import { routes } from '@communities/routes.ts';
import { DeleteDialog, HeroBackground } from '@core/components';
import { withAppInsightsTracking } from '@core/logging/withAppInsightsTracking';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import * as styles from './CommunityDetailPage.css.ts';
import { CommunityHeaderSection } from './sections/CommunityHeaderSection.tsx';

/**
 * Community Detail Page Component
 *
 * Displays detailed information about a community
 */

// eslint-disable-next-line complexity
function CommunityDetailPageComponent() {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuthState();

  const { community, isCommunityAdmin, loading, errors, refetch } = useCommunityDetail();
  const { deleteCommunity, loading: isDeleting, errors: deleteErrors, resetErrors } = useDeleteCommunity(community?.communityGroupId);

  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);

  const handleDeleteConfirm = async () => {
    if (!community) return;

    resetErrors();
    const success = await deleteCommunity(community.id);

    if (success) {
      handleDeleteSuccess();
    }
  };

  // Dialog handlers
  const handleDeleteSuccess = () => {
    setIsDeleteDialogOpen(false);
    // Navigate to the community group page of the deleted community
    if (community?.communityGroupId) {
      navigate(routes.communityGroups.detail(community.communityGroupId));
    } else {
      // Fallback to community groups list if no group ID is available
      navigate(routes.communityGroups.root);
    }
  };

  const handleDeleteCommunity = () => {
    setIsDeleteDialogOpen(true);
  };

  const handleEditCommunity = () => {
    setIsEditDialogOpen(true);
  };

  const handleEditDialogClose = () => {
    setIsEditDialogOpen(false);
  };

  // Authentication check
  if (!isAuthenticated) {
    return <CommunityAuthRequired />;
  }

  // Error states - only show if not loading and have errors
  if (errors && errors.length > 0 && !loading) {
    return <CommunityErrorState errors={errors} onRetry={() => refetch()} />;
  }

  return (
    <div>
      {/* Hero Background Component - always renders */}
      <HeroBackground imageUrl={community?.superHeroArtUrl} />

      {loading ? (
        <CommunityLoadingState />
      ) : !community ? null : (
        <>
          {/* Header Section with Background Image */}
          <CommunityHeaderSection community={community} onDeleteCommunity={isCommunityAdmin ? handleDeleteCommunity : undefined} />

          {/* Content Section with Solid Background */}
          <div className={styles.contentSection}>
            {/* Delete Community Dialog */}
            <DeleteDialog
              description="Are you sure you want to delete this community?"
              errors={deleteErrors}
              isDeleting={isDeleting}
              isOpen={isDeleteDialogOpen}
              itemName={community.name}
              itemType="Community"
              title="Delete Community"
              onClose={() => {
                setIsDeleteDialogOpen(false);
                resetErrors();
              }}
              onConfirm={handleDeleteConfirm}
            />

            {/* Edit Community Dialog */}
            <CommunityDialog initialCommunity={community} isOpen={isEditDialogOpen} onClose={handleEditDialogClose} />

            {/* Tab Content with Navigation */}
            <div className={styles.tabContent}>
              <TabNavigation
                entityId={community.id}
                entityType={EntityType.COMMUNITY}
                isEntityAdmin={isCommunityAdmin}
                onEditCommunity={isCommunityAdmin ? handleEditCommunity : undefined}
              />
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export const CommunityDetailPage = withAppInsightsTracking(CommunityDetailPageComponent, 'CommunityDetailPage');
